/**
 * CourseLayout (TASK-014) — persistent Inertia layout for every reader
 * section (Panoramica / Manuale / Tutor / Verifica / Padronanza). Assigned
 * via the official Inertia persistent-layout pattern:
 *
 *   Page.layout = (page) => <CourseLayout>{page}</CourseLayout>
 *
 * on each `pages/reader/*.tsx` page component — NOT wrapped inside the page
 * body — so Inertia keeps this component mounted across tab navigations
 * (no header/tab flash on every click, see task Success Criteria #3).
 *
 * Owns:
 *   - the course header (serif title + subtitle, from `shell`, plus the
 *     theme toggle and PDF download button — see below).
 *   - CourseTabs (desktop horizontal / mobile fixed bottom bar).
 *   - theme (Studiolo/Notturno) — moved here from Reader.tsx (pre-TASK-014
 *     the reading page alone owned it); now it applies to every section via
 *     CourseThemeContext (./themeContext.tsx), and is reachable from every
 *     tab via the header toggle (post-launch addition — the reading page's
 *     own `ThemePicker`, inside `ReaderTopBar`, still works too and shares
 *     the exact same context/persistence, not a competing source of truth).
 *
 * Reading chrome (TOC sidebar, highlight toolbar, drawer tutor, prev/next,
 * completed/review toggles) stays entirely inside Lettura.tsx — this layout
 * never touches it.
 */
import { useCallback, useState } from 'react'
import type { ReactNode } from 'react'
import { usePage } from '@inertiajs/react'
import type { ThemeId } from '@arianna/reader-ui'
import { readerThemeOptions } from '@arianna/design-system'
import { CourseTabs } from './CourseTabs.js'
import { CourseThemeContext, DEFAULT_COURSE_THEME, useCourseTheme } from './themeContext.js'
import { pdfUrl } from './urls.js'
import type { ShellProp } from './types.js'

const THEME_STORAGE_KEY = 'arianna-theme'

function readThemeFromDom(): ThemeId {
  const attr = document.documentElement.dataset['theme']
  if (attr === 'studiolo' || attr === 'notturno') return attr
  return DEFAULT_COURSE_THEME
}

// Every reader/* page carries `shell` — see ReaderController#shell_prop and
// pages/reader/types.ts. `usePage` requires an index-signature type.
interface PagePropsWithShell {
  shell: ShellProp
  [key: string]: unknown
}

// ---------------------------------------------------------------------------
// ThemeToggleButton — compact Studiolo ↔ Notturno switch, reachable from
// every tab's header (post-launch request: the picker inside Lettura's
// ReaderTopBar was reachable only from the Manuale tab). Only two themes
// exist (see readerThemeOptions), so this is a direct toggle, not a menu —
// one click flips to the other theme. Reads/writes through
// CourseThemeContext, i.e. the SAME state + localStorage persistence
// ReaderTopBar's ThemePicker already uses — not a second source of truth.
//
// Style: a single typographic glyph (a half-filled circle, ◐/◑), no emoji,
// no icon library — same "minimal glyph" register as the rest of this
// header. The glyph itself flips (◐ studiolo → ◑ notturno) so the button
// also communicates state at rest, not just on hover/title.
// ---------------------------------------------------------------------------

const THEME_GLYPH: Record<ThemeId, string> = {
  studiolo: '◐',
  notturno: '◑',
}

function ThemeToggleButton() {
  const { theme, setTheme } = useCourseTheme()
  const next: ThemeId = theme === 'studiolo' ? 'notturno' : 'studiolo'
  const nextLabel = readerThemeOptions.find((option) => option.id === next)?.label ?? next

  return (
    <button
      type="button"
      onClick={() => setTheme(next)}
      title={`Passa al tema ${nextLabel}`}
      aria-label={`Tema attuale: ${theme}. Passa al tema ${nextLabel}.`}
      data-testid="course-theme-toggle"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 32,
        height: 32,
        borderRadius: 'var(--radius-md)',
        border: '1px solid var(--line)',
        background: 'var(--surface)',
        color: 'var(--ink-2)',
        fontSize: 16,
        lineHeight: 1,
        cursor: 'pointer',
      }}
    >
      <span aria-hidden="true">{THEME_GLYPH[theme]}</span>
    </button>
  )
}

// ---------------------------------------------------------------------------
// PdfDownloadButton (TASK-014 Part 2) — course header download button.
// Rendered ONLY when `shell.pdfAvailable` (see ReaderController#shell_prop):
// no PDF attached → no button at all, no fallback, no regeneration prompt
// (explicitly out of scope). A plain `<a>`, not an Inertia `<Link>` — this
// must be a real browser navigation so the server's redirect to the
// ActiveStorage blob (Content-Disposition: attachment) triggers an actual
// download, not an SPA visit.
//
// Style: a single typographic arrow glyph (↓), same minimal register as
// ThemeToggleButton above — no emoji, no icon library.
// ---------------------------------------------------------------------------

function PdfDownloadButton({ manualVersionId }: { manualVersionId: number }) {
  return (
    <a
      href={pdfUrl(manualVersionId)}
      title="Scarica il PDF"
      aria-label="Scarica il PDF del manuale"
      data-testid="course-pdf-download"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 32,
        height: 32,
        borderRadius: 'var(--radius-md)',
        border: '1px solid var(--line)',
        background: 'var(--surface)',
        color: 'var(--ink-2)',
        fontSize: 16,
        lineHeight: 1,
        textDecoration: 'none',
      }}
    >
      <span aria-hidden="true">↓</span>
    </a>
  )
}

interface CourseLayoutProps {
  children: ReactNode
}

export default function CourseLayout({ children }: CourseLayoutProps) {
  const { shell } = usePage<PagePropsWithShell>().props

  const [theme, setThemeState] = useState<ThemeId>(readThemeFromDom)
  const setTheme = useCallback((next: ThemeId) => {
    setThemeState(next)
    document.documentElement.dataset['theme'] = next
    try {
      localStorage.setItem(THEME_STORAGE_KEY, next)
    } catch {
      // localStorage unavailable (private browsing / cross-origin iframe)
    }
  }, [])

  return (
    <CourseThemeContext.Provider value={{ theme, setTheme }}>
      <div
        style={{
          height: '100dvh',
          display: 'flex',
          flexDirection: 'column',
          background: 'var(--bg)',
          overflow: 'hidden',
        }}
      >
        <header
          style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 'var(--space-3)',
            padding: 'var(--space-3) var(--space-5)',
            borderBottom: '1px solid var(--line)',
            background: 'var(--bg)',
            flexShrink: 0,
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
            <div
              style={{
                fontFamily: 'var(--font-mono)',
                fontSize: 10,
                letterSpacing: '0.12em',
                textTransform: 'uppercase',
                color: 'var(--ink-4)',
              }}
            >
              Corso
            </div>
            <div
              style={{
                fontFamily: 'var(--font-serif)',
                fontSize: 20,
                fontWeight: 600,
                letterSpacing: '-0.015em',
                color: 'var(--ink)',
              }}
            >
              {shell.manualTitle}
            </div>
            {shell.manualSubtitle != null && shell.manualSubtitle.length > 0 && (
              <div style={{ fontFamily: 'var(--font-sans)', fontSize: 12, color: 'var(--ink-3)' }}>
                {shell.manualSubtitle}
              </div>
            )}
          </div>

          <div style={{ display: 'flex', gap: 'var(--space-2)', flexShrink: 0, paddingTop: 2 }}>
            {shell.pdfAvailable && <PdfDownloadButton manualVersionId={shell.manualVersionId} />}
            <ThemeToggleButton />
          </div>
        </header>

        <CourseTabs activeTab={shell.activeTab} manualVersionId={shell.manualVersionId} />

        {/* flex-1 + overflow hidden: each page owns its own internal
            scrolling (Lettura's content pane, Overview's page scroll, ...).
            pb-16 on mobile reserves room for the fixed bottom tab bar so it
            never covers the last bit of content; desktop tabs are in normal
            flow so no reserve is needed there. */}
        <div className="flex-1 min-h-0 flex flex-col overflow-hidden pb-16 md:pb-0">{children}</div>
      </div>
    </CourseThemeContext.Provider>
  )
}
